/** German texts of the module, below its ID: `crm.nav.organizations`. */
export const de = {
  name: 'Kunden',
  description:
    'Kunden, Interessenten, Planer, Lieferanten und Behörden der Firma mit Stammdaten, Sperren und USt-Prüfung.',
  nav: { organizations: 'Organisationen', contacts: 'Ansprechpartner' },
  permissions: {
    organization: {
      read: {
        label: 'Organisationen ansehen',
        description: 'Liste und Details der Organisationen öffnen, Verlauf lesen.',
      },
      write: {
        label: 'Organisationen bearbeiten',
        description: 'Organisationen anlegen und ändern, USt-IdNr. prüfen.',
      },
      comment: {
        label: 'Organisationen kommentieren',
        description: 'Kommentare an Organisationen schreiben.',
      },
      remind: {
        label: 'Wiedervorlagen an Organisationen',
        description: 'Eigene Wiedervorlagen an Organisationen setzen.',
      },
      export: {
        label: 'Organisationen exportieren',
        description: 'Die Liste der Organisationen als CSV-Datei herunterladen.',
      },
      archive: {
        label: 'Organisationen archivieren und sperren',
        description: 'Organisationen archivieren, zurückholen, sperren und entsperren.',
      },
      merge: {
        label: 'Dubletten zusammenführen',
        description:
          'Eine Organisation in eine andere überführen; Kommentare und Wiedervorlagen wandern mit.',
      },
    },
    contact: {
      read: {
        label: 'Ansprechpartner ansehen',
        description: 'Liste und Details der Ansprechpartner öffnen, Verlauf lesen.',
      },
      write: {
        label: 'Ansprechpartner bearbeiten',
        description:
          'Ansprechpartner anlegen und ändern, Hauptperson festlegen, als ausgeschieden markieren. Mit Bereich „eigene“ nur selbst angelegte.',
      },
      comment: {
        label: 'Ansprechpartner kommentieren',
        description: 'Kommentare an Ansprechpartnern schreiben.',
      },
      remind: {
        label: 'Wiedervorlagen an Ansprechpartnern',
        description: 'Eigene Wiedervorlagen an Ansprechpartnern setzen.',
      },
      export: {
        label: 'Ansprechpartner exportieren',
        description: 'Die Liste der Ansprechpartner als CSV-Datei herunterladen.',
      },
      delete: {
        label: 'Ansprechpartner löschen',
        description:
          'Ansprechpartner endgültig löschen, z. B. versehentlich angelegte oder doppelte. Name, Kontaktdaten und Kommentare gehen verloren.',
      },
    },
    settings: {
      manage: {
        label: 'Kunden-Einstellungen verwalten',
        description: 'Die Einstellungen des Moduls Kunden für die Firma ändern.',
      },
    },
  },
  roles: {
    reader: 'Kunden: Lesen',
    editor: 'Kunden: Bearbeiten',
    manager: 'Kunden: Verwalten',
  },
  audit: {
    organization: {
      create: 'Organisation angelegt',
      update: 'Organisation geändert',
      archive: 'Organisation archiviert',
      restore: 'Organisation zurückgeholt',
      block: 'Organisation gesperrt',
      unblock: 'Organisation entsperrt',
      'vat-check': 'USt-IdNr. geprüft',
      merge: 'Dublette zusammengeführt',
    },
    address: {
      create: 'Adresse hinzugefügt',
      update: 'Adresse geändert',
      delete: 'Adresse gelöscht',
    },
    contact: {
      create: 'Ansprechpartner angelegt',
      update: 'Ansprechpartner geändert',
      primary: 'Hauptperson geändert',
      leave: 'Ansprechpartner ausgeschieden',
      return: 'Ansprechpartner wieder aktuell',
      delete: 'Ansprechpartner gelöscht',
    },
    list: {
      create: 'Verteiler angelegt',
      update: 'Verteiler geändert',
      delete: 'Verteiler gelöscht',
      'add-members': 'Ansprechpartner in Verteiler aufgenommen',
      'remove-member': 'Ansprechpartner aus Verteiler entfernt',
    },
  },
  sequences: { organization: 'Kundennummer' },
  offline: { organizations: 'Organisationen' },
  guides: {
    organizations: {
      title: 'Organisationen verwalten',
      summary: 'Anlegen, Dubletten vermeiden, sperren, USt-IdNr. prüfen und Wiedervorlagen.',
    },
    contacts: {
      title: 'Ansprechpartner pflegen',
      summary: 'Ansprechpartner anlegen, Hauptperson festlegen, Werbewiderspruch und Ausscheiden.',
    },
  },
  statuses: { active: 'Aktiv', inactive: 'Inaktiv' },
  fields: {
    number: 'Nummer',
    name: 'Name',
    shortName: 'Kurzname',
    status: 'Status',
    industry: 'Branche',
    vatId: 'USt-IdNr.',
    website: 'Website',
    phone: 'Telefon',
    email: 'E-Mail',
    paymentTermDays: 'Zahlungsziel (Tage)',
    datevDebtor: 'DATEV-Debitor',
    accountManager: 'Betreuer',
    city: 'Ort',
    updated: 'Geändert',
  },
  organizations: {
    title: 'Organisationen',
    description: 'Kunden, Interessenten, Planer, Lieferanten und Behörden der Firma.',
    new: 'Neue Organisation',
    search: 'Organisationen durchsuchen',
    searchPlaceholder: 'Name, Nummer, Telefon …',
    allStatuses: 'Alle Status',
    archive: 'Archiv',
    current: 'Aktuell',
    archived: 'Archiviert',
    blocked: 'Gesperrt',
    merged: 'Zusammengeführt',
    exportName: 'Organisationen',
    empty: 'Noch keine Organisationen',
    emptyHint: 'Mit „Neue Organisation“ den ersten Kunden anlegen.',
    emptyReadOnly: 'Angelegte Organisationen erscheinen hier.',
    noMatches: 'Keine passenden Organisationen',
    noMatchesHint: 'Suche oder Filter ändern.',
  },
  organization: {
    masterData: 'Stammdaten',
    masterDataEmpty: 'Noch keine Kontaktdaten, USt-IdNr. oder Zahlungsangaben.',
    changed: 'geändert {{when}}',
    days_one: '{{count}} Tag',
    days_other: '{{count}} Tage',
    notFound: 'Organisation nicht gefunden',
    notFoundHint:
      'Die Adresse ist falsch, die Organisation wurde gelöscht oder gehört zu einer anderen Firma.',
    blockedTitle: 'Gesperrt: nicht beliefern',
    archivedTitle: 'Archiviert',
    archivedSince: 'Seit {{when}}. Archivierte Organisationen fehlen in Auswahlfeldern.',
    mergedTitle: 'Zusammengeführt mit',
    block: 'Sperren',
    blockTitle: 'Organisation sperren',
    blockDescription:
      '„{{name}}“ wird als „nicht beliefern“ markiert. Andere Module zeigen den Grund, bevor sie einen Auftrag annehmen.',
    blockReason: 'Grund',
    blockedToast: 'Organisation gesperrt.',
    unblock: 'Entsperren',
    unblockTitle: 'Sperre aufheben?',
    unblockDescription: '„{{name}}“ kann danach wieder beliefert werden.',
    unblocked: 'Sperre aufgehoben.',
    archive: 'Archivieren',
    archiveTitle: 'Organisation archivieren?',
    archiveDescription:
      '„{{name}}“ verschwindet aus der Liste und aus Auswahlfeldern. Verweise in anderen Modulen bleiben erhalten, zurückholen geht jederzeit.',
    archivedToast: 'Organisation archiviert.',
    restore: 'Zurückholen',
    restored: 'Organisation zurückgeholt.',
    merge: 'Zusammenführen',
    mergeTitle: 'Mit anderer Organisation zusammenführen',
    mergeDescription: 'Für Dubletten: „{{name}}“ wird in die gewählte Organisation überführt.',
    mergeTarget: 'Bleibt erhalten',
    mergeWarning:
      '„{{name}}“ wird danach archiviert. Kommentare und Wiedervorlagen wandern zur gewählten Organisation, Verweise anderer Module werden umgestellt. Rückgängig machen geht nicht.',
    merged: 'Zusammengeführt mit {{number}}.',
    mergedShares_one: '{{count}} Freigabe an andere Firmen wurde dabei aufgehoben.',
    mergedShares_other: '{{count}} Freigaben an andere Firmen wurden dabei aufgehoben.',
  },
  form: {
    createTitle: 'Neue Organisation',
    editTitle: 'Organisation bearbeiten',
    created: 'Organisation {{number}} angelegt.',
    updated: 'Änderungen gespeichert.',
    shortNameHint: 'Wie die Organisation intern genannt wird, z. B. in Listen.',
    vatIdHint: 'Mit Ländercode, z. B. DE123456789.',
    reload: 'Neu laden',
    conflictHint:
      'Neu laden zeigt den aktuellen Stand. Eigene Eingaben in diesem Dialog gehen dabei verloren.',
  },
  duplicates: {
    title_one: 'Möglicherweise schon vorhanden',
    title_other: 'Möglicherweise schon vorhanden ({{count}} Treffer)',
    hint: 'Vor dem Anlegen prüfen. Speichern bleibt möglich.',
    reasons: {
      name: 'ähnlicher Name',
      vatId: 'gleiche USt-IdNr.',
      email: 'gleiche Mail-Domain',
      phone: 'gleiche Telefonnummer',
    },
  },
  contacts: {
    title: 'Ansprechpartner',
    description: 'Personen bei Kunden, Planern, Lieferanten und Behörden.',
    new: 'Neuer Ansprechpartner',
    search: 'Ansprechpartner durchsuchen',
    searchPlaceholder: 'Name, Position, Telefon …',
    allOrganizations: 'Alle Organisationen',
    state: 'Stand',
    current: 'Aktuell',
    left: 'Ausgeschieden',
    primary: 'Hauptperson',
    objection: 'Werbewiderspruch',
    anonymized: 'Anonymisiert',
    exportName: 'Ansprechpartner',
    empty: 'Noch keine Ansprechpartner',
    emptyHint: 'Mit „Neuer Ansprechpartner“ die erste Person anlegen.',
    emptyReadOnly: 'Angelegte Ansprechpartner erscheinen hier.',
    noMatches: 'Keine passenden Ansprechpartner',
    noMatchesHint: 'Suche oder Filter ändern.',
    ofOrganization: 'Ansprechpartner von {{name}}',
    emptyOrganization: 'Keine Ansprechpartner',
    emptyOrganizationHint: 'Mit „Neuer Ansprechpartner“ eine Person dieser Organisation anlegen.',
  },
  contact: {
    data: 'Kontaktdaten',
    notFound: 'Ansprechpartner nicht gefunden',
    notFoundHint:
      'Die Adresse ist falsch, der Ansprechpartner wurde gelöscht oder gehört zu einer anderen Firma.',
    teamsChat: 'Chat in Teams',
    actionsFor: 'Aktionen für {{name}}',
    makePrimary: 'Als Hauptperson festlegen',
    removePrimary: 'Nicht mehr Hauptperson',
    primarySet: 'Hauptperson festgelegt.',
    primaryRemoved: 'Keine Hauptperson mehr.',
    leave: 'Ausgeschieden',
    leaveTitle: 'Als ausgeschieden markieren?',
    leaveDescription:
      '{{name}} arbeitet nicht mehr bei {{organization}}. Der Eintrag bleibt mit Verlauf erhalten, ist aber keine Hauptperson mehr und fehlt in Auswahlfeldern.',
    leftToast: 'Als ausgeschieden markiert.',
    return: 'Wieder aktuell',
    returned: 'Wieder als aktuell geführt.',
    leftTitle: 'Ausgeschieden',
    leftSince: 'Seit {{when}}.',
    objectionTitle: 'Werbewiderspruch: keine Werbung senden',
    objectionSince: 'Erfasst {{when}}. Gilt für Newsletter, Mailings und Werbeanrufe.',
    anonymizedTitle: 'Anonymisiert',
    anonymizedHint:
      'Nach Ablauf der Löschfrist wurden Name und Kontaktdaten entfernt. Der Eintrag lässt sich nicht mehr ändern.',
    delete: 'Löschen',
    deleteTitle: 'Ansprechpartner löschen?',
    deleteDescription:
      '{{name}} wird mit Kontaktdaten, Kommentaren und Wiedervorlagen gelöscht. Das lässt sich nicht rückgängig machen. Einträge, die auf die Person verweisen, z. B. Projekte, zeigen danach „Nicht verfügbar“. Arbeitet die Person nur nicht mehr dort, besser „Ausgeschieden“ wählen.',
    deleted: 'Ansprechpartner gelöscht.',
  },
  contactFields: {
    organization: 'Organisation',
    salutation: 'Anrede',
    title: 'Titel',
    firstName: 'Vorname',
    lastName: 'Nachname',
    name: 'Name',
    position: 'Position',
    department: 'Abteilung',
    email: 'E-Mail',
    phone: 'Telefon',
    mobile: 'Mobil',
    language: 'Sprache',
    primary: 'Hauptperson',
    marketingObjection: 'Werbewiderspruch',
    state: 'Stand',
  },
  contactForm: {
    createTitle: 'Neuer Ansprechpartner',
    editTitle: 'Ansprechpartner bearbeiten',
    created: 'Ansprechpartner angelegt.',
    updated: 'Änderungen gespeichert.',
    primaryHint:
      'Erste Ansprechperson der Organisation. Eine bisherige Hauptperson verliert die Rolle.',
    objectionHint: 'Die Person hat der Nutzung ihrer Daten für Werbung widersprochen.',
  },
  contactPicker: {
    placeholder: 'Ansprechpartner auswählen',
    search: 'Name suchen',
    empty: 'Kein Ansprechpartner gefunden.',
    create: 'Ansprechpartner anlegen',
    createNamed: '„{{name}}“ als Ansprechpartner anlegen',
  },
  salutations: { mr: 'Herr', ms: 'Frau', none: 'Keine Anrede' },
  languages: {
    none: 'Nicht angegeben',
    de: 'Deutsch',
    en: 'Englisch',
    fr: 'Französisch',
    it: 'Italienisch',
    nl: 'Niederländisch',
    pl: 'Polnisch',
  },
  picker: {
    placeholder: 'Organisation auswählen',
    search: 'Name oder Nummer suchen',
    empty: 'Keine Organisation gefunden.',
    create: 'Organisation anlegen',
    createNamed: '„{{name}}“ als Organisation anlegen',
    loading: 'Wird geladen …',
    blocked: 'Gesperrt: {{reason}}',
    unavailable: 'Nicht verfügbar',
  },
  errors: {
    name: 'Name eingeben.',
    vatId: 'USt-IdNr. mit Ländercode eingeben, z. B. DE123456789.',
    website: 'Gültige Adresse eingeben, z. B. www.beispiel.de.',
    phone: 'Nur Ziffern, Leerzeichen und + ( ) / . - verwenden.',
    email: 'Gültige E-Mail-Adresse eingeben.',
    paymentTermDays: 'Ganze Zahl von 0 bis 365 eingeben.',
    datevDebtor: 'Bis zu 20 Buchstaben, Ziffern oder Bindestriche.',
    accountManager: 'Keine aktive Person der Firma. Eine andere Person auswählen.',
    blockReason: 'Grund für die Sperre eingeben.',
    countryCode: 'Ländercode mit zwei Buchstaben eingeben, z. B. DE.',
    coordinates: 'Koordinaten unvollständig. Adresse erneut über die Suche wählen.',
    invalid: 'Eingabe prüfen.',
    invalidInput: 'Eingaben prüfen.',
    noVatId: 'Keine USt-IdNr. hinterlegt.',
    vatNotEu: 'VIES prüft nur USt-IdNr. aus der EU.',
    anonymized: 'Die Organisation wurde anonymisiert und kann nicht mehr geändert werden.',
    merged: 'Die Organisation wurde zusammengeführt und ist nur noch lesbar.',
    mergeTarget:
      'Diese Organisation kommt als Ziel nicht in Frage, weil sie archiviert oder selbst zusammengeführt ist.',
    mergeTargetMissing: 'Ziel auswählen.',
    versionConflict: 'Inzwischen von jemand anderem geändert.',
    notFound: 'Organisation nicht gefunden. Vielleicht wurde sie gelöscht.',
    forbidden: 'Dafür fehlt eine Berechtigung.',
    rateLimited: 'Zu viele Anfragen, bitte gleich noch einmal versuchen.',
    lastName: 'Nachnamen eingeben.',
    organization: 'Organisation auswählen.',
    contactOrganization:
      'Die Organisation gibt es nicht mehr oder sie wurde zusammengeführt. Eine andere wählen.',
    contactAnonymized:
      'Der Ansprechpartner wurde anonymisiert und kann nicht mehr geändert werden.',
    contactLeft: 'Ausgeschiedene können keine Hauptperson sein.',
    contactNotFound: 'Ansprechpartner nicht gefunden. Vielleicht wurde er gelöscht.',
  },
  addressKinds: {
    main: 'Hauptsitz',
    billing: 'Rechnungsadresse',
    delivery: 'Lieferadresse',
    site: 'Baustelle',
    other: 'Sonstige',
  },
  addresses: {
    title: 'Adressen',
    add: 'Adresse hinzufügen',
    none: 'Noch keine Adresse.',
    edit: 'Adresse ändern',
    remove: 'Adresse löschen',
    removeTitle: 'Adresse löschen?',
    removeDescription: 'Die Adresse verschwindet aus der Organisation.',
    removed: 'Adresse gelöscht.',
    saved: 'Adresse gespeichert.',
    addTitle: 'Adresse hinzufügen',
    editTitle: 'Adresse ändern',
    map: 'Karte der Adressen von {{name}}',
    search: 'Adresse suchen',
    searchHint: 'Füllt die Felder unten aus. Ohne Treffer die Felder direkt ausfüllen.',
    pointHint:
      'Setzt die Adresssuche. Passt er nicht, z. B. bei einem großen Gelände, den Punkt auf der Karte verschieben oder Koordinaten eingeben.',
    fields: {
      kind: 'Art',
      label: 'Bezeichnung',
      street: 'Straße',
      houseNumber: 'Hausnummer',
      extra: 'Zusatz',
      postcode: 'PLZ',
      city: 'Ort',
      countryCode: 'Land (Code)',
    },
  },
  map: {
    open: 'Karte',
    list: 'Liste',
    views: 'Darstellung',
    title: 'Organisationen auf der Karte',
    description:
      'Hauptsitze der aktuellen Organisationen, deren Adresse über die Suche gewählt wurde.',
    empty: 'Keine Organisation auf der Karte',
    emptyHint:
      'Hauptsitze erscheinen hier, sobald ihre Adresse über die Adresssuche gewählt wurde.',
  },
  import: {
    organizations: 'Organisationen importieren',
    errors: {
      status: 'Unbekannter Status. Erlaubt: aktiv, inaktiv.',
    },
  },
};
